import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical,
  MessageSquare,
  BarChart3
} from 'lucide-react';
import QuickQuestionModal from '../components/QuickQuestionModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import toast from 'react-hot-toast';

const QuickQuestions = () => {
  const { id: clientId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);

  const { data, isLoading, refetch } = useQuery(
    ['quickQuestions', clientId],
    async () => {
      const response = await axios.get(`/quick-questions/client/${clientId}`);
      return response.data;
    }
  );

  const { data: clientData, refetch: refetchClient } = useQuery(
    ['client', clientId],
    async () => {
      const response = await axios.get(`/clients/${clientId}`);
      return response.data;
    }
  );

  const handleDelete = async (questionId) => {
    try {
      await axios.delete(`/quick-questions/${questionId}`);
      toast.success('Quick question deleted successfully');
      refetch();
      setDeletingQuestion(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete question');
    }
  };

  const handleContactFormToggle = async (e) => {
    try {
      const showContactForm = e.target.checked;
      await axios.put(`/clients/${clientId}`, {
        chatbotConfig: {
          ...clientData?.chatbotConfig,
          showContactForm
        }
      });
      toast.success('Contact form setting updated successfully');
      refetchClient();
    } catch (error) {
      toast.error('Failed to update contact form setting');
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingQuestion(null);
  };

  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-300 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const questions = data?.questions || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to={`/clients/${clientId}`}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Client
        </Link>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quick Questions</h1>
            <p className="text-gray-600">Manage chatbot quick question bubbles</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </button>
        </div>
      </div>

      {/* Info Box */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <MessageSquare className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">About Quick Questions</h3>
            <p className="mt-1 text-sm text-blue-700">
              Quick question bubbles appear in the chat widget below the welcome message. 
              Users can click these bubbles to get instant pre-defined answers without typing.
              Maximum 6 bubbles will be shown to avoid clutter.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Form Setting */}
      <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={clientData?.chatbotConfig?.showContactForm || false}
            onChange={handleContactFormToggle}
            className="mt-1 mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
          />
          <div>
            <span className="text-sm font-medium text-green-800">
              Show "Susisiekite su manimi" contact form button
            </span>
            <p className="text-sm text-green-700 mt-1">
              When enabled, users will see a contact form button that allows them to send you their contact details and messages directly.
            </p>
          </div>
        </label>
      </div>

      {/* Questions Grid */}
      {questions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {questions.map((question, index) => (
            <div
              key={question._id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow border p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <GripVertical className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-xs text-gray-500">#{index + 1}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleEdit(question)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeletingQuestion(question)}
                    className="text-gray-400 hover:text-red-600 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="inline-block bg-primary-50 border border-primary-200 text-primary-700 px-3 py-1 rounded-full text-sm font-medium mb-2">
                  {question.title}
                </div>
                <p className="text-sm text-gray-600 line-clamp-3">
                  {question.response}
                </p>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center">
                  <BarChart3 className="h-3 w-3 mr-1" />
                  {question.clickCount || 0} clicks
                </div>
                <div>
                  Order: {question.order}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No quick questions yet</h3>
          <p className="text-gray-500 mb-4">
            Create quick question bubbles to help users get instant answers to common questions.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add First Question
          </button>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <QuickQuestionModal
          question={editingQuestion}
          clientId={clientId}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {deletingQuestion && (
        <DeleteConfirmModal
          title="Delete Quick Question"
          message={`Are you sure you want to delete "${deletingQuestion.title}"? This action cannot be undone.`}
          onConfirm={() => handleDelete(deletingQuestion._id)}
          onCancel={() => setDeletingQuestion(null)}
        />
      )}
    </div>
  );
};

export default QuickQuestions;